<template>
  <div class="company_new_account-container company_new_account-main glass-tab-page company_new_account-page--glass">
    <BrucdcumHeader :breadcrumb="['Company', isEdit ? 'Edit Account' : 'New Account']" />

    <form class="company_new_account-form" @submit.prevent>
      <div class="glass-tab-view glass-tab-view--flush company_new_account-glass-shell">
        <div class="glass-tab-view__content company_new_account-page-stack">
          <div class="company_new_account-header-block">
            <h2 class="company_new_account-title">{{ dynamicTitle }}</h2>
            <p class="company_new_account-subtitle">
              {{ isEdit ? 'Update account details and balances' : 'Create a new chart of accounts entry' }}
            </p>
          </div>

          <div
            class="company_new_account-glass-panel glass-tab-panel glass-tab-panel--flex-column glass-tab-panel--stagger-rows"
          >
            <!-- Row 1: Account Type, Account Number -->
            <div class="glass-tab-panel__row company_new_account-form-group-row company_new_account-form-group-row--cols-2">
              <div class="company_new_account-form-group company_new_account-form-group--dropdown">
                <LabelDropdown
                  label="Account Type"
                  select-id="new-account-type"
                  v-model="accountType"
                  flex
                  wide
                  searchable
                  search-placeholder="Search account types..."
                  :options="accountTypeDropdownOptions"
                />
              </div>
              <div class="company_new_account-form-group company_new_account-form-group--input">
                <LabelInputField
                  input-id="new-account-number"
                  label="Account Number"
                  v-model="accountNumber"
                  required
                  flex
                  readonly
                  :has-error="!!fieldErrors.accountNumber"
                >
                  <div v-if="fieldErrors.accountNumber" class="company_new_account-error-text">
                    {{ fieldErrors.accountNumber }}
                  </div>
                </LabelInputField>
              </div>
            </div>

            <!-- Row 2: Account Name, As of -->
            <div class="glass-tab-panel__row company_new_account-form-group-row company_new_account-form-group-row--cols-2">
              <div class="company_new_account-form-group company_new_account-form-group--input">
                <LabelInputField
                  input-id="new-account-name"
                  label="Account Name"
                  v-model="accountName"
                  required
                  flex
                  :has-error="!!fieldErrors.accountName"
                >
                  <div v-if="fieldErrors.accountName" class="company_new_account-error-text">
                    {{ fieldErrors.accountName }}
                  </div>
                </LabelInputField>
              </div>
              <div class="company_new_account-form-group company_new_account-form-group--date">
                <LabelDateField
                  input-id="new-account-as-of"
                  label="As of"
                  v-model="asOf"
                  placeholder="MM/DD/YYYY"
                  flex
                />
              </div>
            </div>

            <!-- Row 3: Opening Balance, Current Balance -->
            <div class="glass-tab-panel__row company_new_account-form-group-row company_new_account-form-group-row--cols-2">
              <div class="company_new_account-form-group company_new_account-form-group--input">
                <LabelInputField
                  input-id="new-account-opening-balance"
                  label="Opening Balance (USD)"
                  v-model="openingBalance"
                  type="number"
                  flex
                  :readonly="isEdit"
                />
              </div>
              <div class="company_new_account-form-group company_new_account-form-group--input">
                <LabelInputField
                  input-id="new-account-current-balance"
                  label="Current Balance (USD)"
                  :model-value="computedCurrentBalance"
                  flex
                  readonly
                />
              </div>
            </div>

            <!-- Row 4: PayPal Email (conditional), checkboxes -->
            <div class="glass-tab-panel__row company_new_account-form-group-row company_new_account-form-group-row--checkboxes">
              <div
                v-if="accountType === 'Paypal'"
                class="company_new_account-form-group company_new_account-form-group--input"
              >
                <LabelInputField
                  input-id="paypal-email"
                  label="PayPal Email"
                  v-model="paypalEmail"
                  type="email"
                  autocomplete="email"
                  flex
                />
              </div>
              <div class="company_new_account-form-group company_new_account-form-group--checkbox">
                <LabelCheckboxField
                  input-id="new-account-sub"
                  label="Is Sub-Account?"
                  v-model="isSubAccount"
                  flex
                />
              </div>
              <div class="company_new_account-form-group company_new_account-form-group--checkbox">
                <LabelCheckboxField
                  input-id="new-account-active"
                  label="Active"
                  v-model="active"
                  flex
                />
              </div>
              <div class="company_new_account-form-group company_new_account-form-group--checkbox">
                <LabelCheckboxField
                  input-id="new-account-depreciation"
                  label="Is Depreciation"
                  v-model="isDepreciation"
                  flex
                />
              </div>
            </div>

            <!-- Parent Account, Credit Card fields -->
            <div
              v-if="isSubAccount || accountType === 'Credit Card'"
              class="glass-tab-panel__row company_new_account-form-group-row company_new_account-form-group-row--cols-3"
            >
              <div v-if="isSubAccount" class="company_new_account-form-group company_new_account-form-group--dropdown">
                <LabelDropdown
                  label="Parent Account"
                  select-id="new-account-parent"
                  v-model="parentAccountId"
                  flex
                  wide
                  required
                  searchable
                  search-placeholder="Search parent accounts..."
                  :options="parentAccountDropdownOptions"
                  :has-error="!!fieldErrors.parentAccount || !!parentAccountError"
                >
                  <div v-if="fieldErrors.parentAccount" class="company_new_account-error-text">
                    {{ fieldErrors.parentAccount }}
                  </div>
                  <div v-else-if="parentAccountError" class="company_new_account-error-text">
                    {{ parentAccountError }}
                  </div>
                </LabelDropdown>
              </div>
              <div v-if="accountType === 'Credit Card'" class="company_new_account-form-group company_new_account-form-group--input">
                <LabelInputField
                  input-id="new-account-credit-limit"
                  label="Credit Limit (USD)"
                  v-model="creditLimit"
                  type="number"
                  flex
                />
              </div>
              <div v-if="accountType === 'Credit Card'" class="company_new_account-form-group company_new_account-form-group--input">
                <LabelInputField
                  input-id="new-account-card-number"
                  label="Card or Loan Number"
                  v-model="cardOrLoanNumber"
                  flex
                />
              </div>
            </div>

            <!-- Bank fields -->
            <div
              v-if="accountType === 'Bank'"
              class="glass-tab-panel__row company_new_account-form-group-row company_new_account-form-group-row--cols-3"
            >
              <div class="company_new_account-form-group company_new_account-form-group--input">
                <LabelInputField
                  input-id="new-account-bank-name"
                  label="Bank Name"
                  v-model="bankName"
                  required
                  flex
                  :has-error="!!fieldErrors.bankName"
                >
                  <div v-if="fieldErrors.bankName" class="company_new_account-error-text">
                    {{ fieldErrors.bankName }}
                  </div>
                </LabelInputField>
              </div>
              <div class="company_new_account-form-group company_new_account-form-group--dropdown">
                <LabelDropdown
                  label="Bank Account Type"
                  select-id="new-account-bank-type"
                  v-model="bankAccountType"
                  flex
                  wide
                  :searchable="false"
                  :options="bankAccountTypeDropdownOptions"
                  :has-error="!!fieldErrors.bankAccountType"
                >
                  <div v-if="fieldErrors.bankAccountType" class="company_new_account-error-text">
                    {{ fieldErrors.bankAccountType }}
                  </div>
                </LabelDropdown>
              </div>
              <div class="company_new_account-form-group company_new_account-form-group--input">
                <LabelInputField
                  input-id="new-account-bank-number"
                  label="Bank Account Number"
                  v-model="bankAccountNumber"
                  required
                  flex
                  :has-error="!!fieldErrors.bankAccountNumber"
                >
                  <div v-if="fieldErrors.bankAccountNumber" class="company_new_account-error-text">
                    {{ fieldErrors.bankAccountNumber }}
                  </div>
                </LabelInputField>
              </div>
            </div>

            <!-- Deposit Amount (edit mode) -->
            <div
              v-if="isEdit"
              class="glass-tab-panel__row company_new_account-form-group-row company_new_account-form-group-row--cols-2"
            >
              <div class="company_new_account-form-group company_new_account-form-group--input">
                <LabelInputField
                  input-id="new-account-deposit"
                  label="Deposit Amount (USD)"
                  v-model="depositAmount"
                  type="number"
                  flex
                />
              </div>
            </div>

            <!-- Comments -->
            <div class="glass-tab-panel__row company_new_account-form-group-row company_new_account-form-group-row--full">
              <div class="company_new_account-form-group company_new_account-form-group--description">
                <LabelDescriptionField
                  input-id="new-account-comments"
                  label="Comments"
                  v-model="comments"
                  :rows="4"
                  flex
                />
              </div>
            </div>
          </div>

          <div class="company_new_account-form-actions glass-tab-view__actions">
            <SecondaryButton label="Cancel" :disabled="saving" @click="cancel" />
            <SecondaryButton
              v-if="!isEdit"
              label="Save and New"
              icon="bi bi-plus-circle"
              :disabled="saving"
              @click="submitAccount(true)"
            />
            <PrimaryButton
              :label="isEdit ? 'Update and Close' : 'Save and Close'"
              icon="bi bi-check-lg"
              :loading="saving"
              @click="submitAccount(false)"
            />
          </div>
        </div>
      </div>
    </form>

    <ErrorModal
      :show="showErrorModal"
      :message="errorMessage"
      @close="closeErrorModal"
    />
  </div>
</template>

<script>
import '../styles/NewAccount.css';
import axios from 'axios';
import logger from '@/utils/logger';
import ApiService from '@/services/api.service.js';
import ErrorModal from '@/components/ErrorModal.vue';
import BrucdcumHeader from '@/components/BrucdcumHeader.vue';
import LabelInputField from '@/modules/consultant/components/LabelInputField.vue';
import LabelDropdown from '@/modules/consultant/components/LabelDropdown.vue';
import LabelDateField from '@/modules/consultant/components/LabelDateField.vue';
import LabelCheckboxField from '@/modules/consultant/components/LabelCheckboxField.vue';
import LabelDescriptionField from '@/modules/consultant/components/LabelDescriptionField.vue';
import PrimaryButton from '@/modules/consultant/components/PrimaryButton.vue';
import SecondaryButton from '@/modules/consultant/components/SecondaryButton.vue';

const ACCOUNT_TYPE_GROUPS = [
  { header: 'REVENUE & INCOME', types: ['Income', 'Other Income'] },
  { header: 'EXPENSES & COSTS', types: ['Expense', 'Other Expense', 'Cost of Goods Sold'] },
  { header: 'ASSETS & RESOURCES', types: ['Cash', 'Bank', 'Other current Asset', 'Inventory Asset', 'Other Assets', 'Fixed Asset'] },
  { header: 'LIABILITIES & OBLIGATIONS', types: ['Payroll Liability', 'Current Liability', 'Long Term Liability'] },
  { header: 'SPECIAL ACCOUNTS', types: ['Credit Card', 'Equity', 'Paypal'] },
];

const ACCOUNT_TYPE_LABELS = {
  'Cost of Goods Sold': 'Cost of Goods Sold (COGS)',
};

export default {
  name: 'NewAccount',
  components: {
    ErrorModal,
    BrucdcumHeader,
    LabelInputField,
    LabelDropdown,
    LabelDateField,
    LabelCheckboxField,
    LabelDescriptionField,
    PrimaryButton,
    SecondaryButton,
  },
  data() {
    return {
      accountType: 'Income',
      accountTypeOptions: [
        'Income',
        'Other Income',
        'Expense',
        'Other Expense',
        'Cost of Goods Sold',
        'Cash',
        'Bank',
        'Other current Asset',
        'Inventory Asset',
        'Other Assets',
        'Fixed Asset',
        'Credit Card',
        'Payroll Liability',
        'Current Liability',
        'Long Term Liability',
        'Equity',
        'Paypal',
      ],
      accountNumber: '',
      accountName: '',
      bankName: '',
      bankAccountType: '',
      bankAccountTypeOptions: ['Checking', 'Money Market', 'Saving'],
      bankAccountNumber: '',
      creditLimit: '',
      cardOrLoanNumber: '',
      paypalEmail: '',
      active: true,
      isSubAccount: false,
      isDepreciation: false,
      asOf: '',
      comments: '',
      parentAccountId: '',
      parentAccounts: [],
      openingBalance: 0.0,
      backendCurrentBalance: 0.0,
      isEdit: false,
      editAccountId: null,
      depositAmount: 0,
      parentAccountError: '',
      showErrorModal: false,
      errorMessage: '',
      saving: false,
      fieldErrors: {
        accountNumber: '',
        accountName: '',
        bankName: '',
        bankAccountType: '',
        bankAccountNumber: '',
        parentAccount: '',
      },
      existingAccounts: [],
    };
  },
  computed: {
    dynamicTitle() {
      return this.isEdit ? 'Edit Account' : `${this.accountType} Account`;
    },
    computedCurrentBalance() {
      if (this.isEdit) {
        const currentBalance = Number(this.backendCurrentBalance) + Number(this.depositAmount || 0);
        return currentBalance.toFixed(2);
      }
      return Number(this.openingBalance).toFixed(2);
    },
    accountTypeDropdownOptions() {
      const options = [];
      ACCOUNT_TYPE_GROUPS.forEach((group) => {
        options.push({ value: `__header_${group.header}`, label: group.header, disabled: true });
        group.types.forEach((type) => {
          options.push({
            value: type,
            label: ACCOUNT_TYPE_LABELS[type] || type,
          });
        });
      });
      return options;
    },
    parentAccountDropdownOptions() {
      return [
        { value: '', label: 'Select Parent Account', disabled: true },
        ...this.parentAccounts.map((account) => ({
          value: account.id,
          label: `${account.accountName} (${account.accountNumber})`,
        })),
      ];
    },
    bankAccountTypeDropdownOptions() {
      return this.bankAccountTypeOptions.map((type) => ({ value: type, label: type }));
    },
  },
  methods: {
    async fetchAccountForEdit() {
      try {
        const res = await ApiService.getCompanyAccountById(this.editAccountId);
        const acc = res.data.account || res.data;
        this.accountType = acc.accountType || acc.type || '';
        this.accountNumber = acc.accountNumber || acc.no || '';
        this.accountName = acc.accountName || acc.name || '';
        this.bankName = acc.bankName || '';
        this.bankAccountType = acc.bankAccountType || '';
        this.bankAccountNumber = acc.bankAccountNumber || '';
        this.creditLimit = acc.creditLimit || '';
        this.cardOrLoanNumber = acc.cardOrLoanNumber || '';
        this.paypalEmail = acc.paypalEmail || '';
        this.active = acc.active !== undefined ? acc.active : true;
        this.isSubAccount = acc.isSubAccount || false;
        this.isDepreciation = acc.isDepreciation || false;
        this.asOf = acc.asOf ? this.formatDateForDatePicker(new Date(acc.asOf)) : this.formatDateForDatePicker(new Date());
        this.comments = acc.comments || '';
        this.parentAccountId = acc.parentAccountId || '';
        this.openingBalance = acc.openingBalance || 0.0;
        this.backendCurrentBalance = acc.currentBalance || 0.0;
      } catch (err) {
        this.showError('Failed to load account for editing. Please try again.');
      }
    },
    formatDateForDatePicker(date) {
      if (!date) return '';
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();
      return `${month}/${day}/${year}`;
    },
    parseDateFromDatePicker(dateString) {
      if (!dateString || !dateString.includes('/')) return new Date();
      const parts = dateString.split('/');
      if (parts.length === 3) {
        const month = parseInt(parts[0], 10) - 1;
        const day = parseInt(parts[1], 10);
        const year = parseInt(parts[2], 10);
        return new Date(year, month, day);
      }
      return new Date();
    },
    convertDateForBackend(dateString) {
      if (!dateString) return '';
      const date = this.parseDateFromDatePicker(dateString);
      const yyyy = date.getFullYear();
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const dd = String(date.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    },
    validateParentAccount() {
      if (this.isSubAccount && (!this.parentAccountId || this.parentAccountId === '')) {
        this.fieldErrors.parentAccount =
          'Parent Account is required when creating a sub-account. Please select a parent account.';
        this.parentAccountError =
          'Parent Account is required when creating a sub-account. Please select a parent account.';
        return false;
      }
      this.fieldErrors.parentAccount = '';
      this.parentAccountError = '';
      return true;
    },
    async fetchExistingAccounts() {
      try {
        const companyId = this.$route.params.id;
        const res = await ApiService.getCompanyAccounts(companyId);
        this.existingAccounts = res.data.accounts || res.data || [];
        logger.log('Fetched existing accounts:', this.existingAccounts.length);
        if (this.accountType && !this.isEdit && (!this.accountNumber || this.accountNumber.trim() === '')) {
          await this.generateNextAccountNumber();
        }
      } catch (error) {
        logger.error('Failed to fetch existing accounts:', error);
        this.existingAccounts = [];
      }
    },
    getAccountTypeRange(accountType) {
      const ranges = {
        Cash: { start: 1000, end: 1009 },
        Bank: { start: 1010, end: 1019 },
        Paypal: { start: 1020, end: 1029 },
        'Other current Asset': { start: 1200, end: 1299 },
        'Inventory Asset': { start: 1300, end: 1399 },
        'Other Assets': { start: 1400, end: 1499 },
        'Fixed Asset': { start: 1500, end: 1599 },
        'Credit Card': { start: 1600, end: 1699 },
        'Payroll Liability': { start: 2000, end: 2099 },
        'Current Liability': { start: 2100, end: 2199 },
        'Long Term Liability': { start: 2200, end: 2299 },
        Equity: { start: 3000, end: 3099 },
        Income: { start: 4000, end: 4099 },
        'Other Income': { start: 4100, end: 4199 },
        Expense: { start: 5000, end: 5099 },
        'Other Expense': { start: 5100, end: 5199 },
        'Cost of Goods Sold': { start: 6000, end: 6099 },
      };
      return ranges[accountType] || { start: 1000, end: 9999 };
    },
    async generateNextAccountNumber() {
      if (!this.accountType || this.isEdit) return;

      const range = this.getAccountTypeRange(this.accountType);
      const accountsOfSameType = this.existingAccounts.filter((acc) => {
        const accType = acc.accountType || acc.type || '';
        return accType === this.accountType;
      });

      const accountNumbers = accountsOfSameType
        .map((acc) => {
          const accNum = acc.accountNumber || acc.no || acc.account_number || acc.number || '';
          const num = parseInt(accNum, 10);
          return Number.isNaN(num) ? null : num;
        })
        .filter((num) => num !== null && num >= range.start && num <= range.end);

      let nextNumber = range.start;
      if (accountNumbers.length > 0) {
        const maxNumber = Math.max(...accountNumbers);
        nextNumber = maxNumber + 1;
        if (nextNumber > range.end) {
          nextNumber = this.findNextAvailableInRange(range, accountNumbers);
        }
      }

      this.accountNumber = String(nextNumber);
    },
    findNextAvailableInRange(range, usedNumbers) {
      for (let num = range.start; num <= range.end; num++) {
        if (!usedNumbers.includes(num)) return num;
      }
      return range.end;
    },
    validateDuplicateAccount() {
      if (!this.accountNumber.trim() || !this.accountName.trim()) return true;

      const accountNumber = this.accountNumber.trim();
      const accountName = this.accountName.trim();
      const bankAccountNumber = this.bankAccountNumber.trim();
      const isOtherAccount = (acc) => {
        if (!this.isEdit || !this.editAccountId) return true;
        return String(acc.id) !== String(this.editAccountId);
      };

      if (!this.isEdit) {
        const duplicateNumber = this.existingAccounts.find((acc) => {
          const accNum = acc.accountNumber || acc.no || acc.account_number || acc.number || '';
          return String(accNum).trim() === accountNumber;
        });
        if (duplicateNumber) {
          this.fieldErrors.accountNumber = `Account number "${accountNumber}" already exists.`;
          return false;
        }
      }

      const duplicateName = this.existingAccounts.find((acc) => {
        if (!isOtherAccount(acc)) return false;
        const accName = acc.accountName || acc.name || '';
        return String(accName).trim().toLowerCase() === accountName.toLowerCase();
      });
      if (duplicateName) {
        this.fieldErrors.accountName = `Account name "${accountName}" already exists.`;
        return false;
      }

      if (this.accountType === 'Bank' && bankAccountNumber) {
        const duplicateBank = this.existingAccounts.find((acc) => {
          if (!isOtherAccount(acc)) return false;
          const bankNum = acc.bankAccountNumber || '';
          return String(bankNum).trim() === bankAccountNumber;
        });
        if (duplicateBank) {
          this.fieldErrors.bankAccountNumber = `Bank account number "${bankAccountNumber}" already exists.`;
          return false;
        }
      }

      return true;
    },
    validateForm() {
      let isValid = true;
      const errorMessages = [];

      this.fieldErrors = {
        accountNumber: '',
        accountName: '',
        bankName: '',
        bankAccountType: '',
        bankAccountNumber: '',
        parentAccount: '',
      };

      if (!this.accountNumber || !this.accountNumber.trim()) {
        this.fieldErrors.accountNumber = 'Account Number is required.';
        errorMessages.push('Account Number is required.');
        isValid = false;
      }

      if (!this.accountName || !this.accountName.trim()) {
        this.fieldErrors.accountName = 'Account Name is required.';
        errorMessages.push('Account Name is required.');
        isValid = false;
      }

      if (this.accountType === 'Bank') {
        if (!this.bankName || !this.bankName.trim()) {
          this.fieldErrors.bankName = 'Bank Name is required for Bank accounts.';
          errorMessages.push('Bank Name is required.');
          isValid = false;
        }
        if (!this.bankAccountType) {
          this.fieldErrors.bankAccountType = 'Bank Account Type is required.';
          errorMessages.push('Bank Account Type is required.');
          isValid = false;
        }
        if (!this.bankAccountNumber || !this.bankAccountNumber.trim()) {
          this.fieldErrors.bankAccountNumber = 'Bank Account Number is required.';
          errorMessages.push('Bank Account Number is required.');
          isValid = false;
        }
      }

      if (!this.validateParentAccount()) {
        errorMessages.push(this.fieldErrors.parentAccount);
        isValid = false;
      }

      if (!this.validateDuplicateAccount()) {
        isValid = false;
        if (this.fieldErrors.accountNumber) errorMessages.push(this.fieldErrors.accountNumber);
        if (this.fieldErrors.accountName) errorMessages.push(this.fieldErrors.accountName);
        if (this.fieldErrors.bankAccountNumber) errorMessages.push(this.fieldErrors.bankAccountNumber);
      }

      if (!isValid) {
        const errorMessage =
          errorMessages.length > 0 ? errorMessages.join('\n') : 'Please fill in all required fields';
        this.showError(errorMessage);
      }

      return isValid;
    },
    async submitAccount(goToNew) {
      if (this.saving) return;

      this.saving = true;
      try {
        await this.fetchExistingAccounts();

        if (!this.validateForm()) {
          return;
        }

        const companyId = this.$route.params.id;
        const payload = {
          companyId,
          accountType: this.accountType,
          accountNumber: this.accountNumber,
          accountName: this.accountName,
          bankName: this.accountType === 'Bank' ? this.bankName : null,
          bankAccountType: this.accountType === 'Bank' ? this.bankAccountType : null,
          bankAccountNumber: this.accountType === 'Bank' ? this.bankAccountNumber : null,
          creditLimit: this.accountType === 'Credit Card' ? (this.creditLimit === '' ? null : this.creditLimit) : null,
          cardOrLoanNumber: this.accountType === 'Credit Card' ? this.cardOrLoanNumber : null,
          paypalEmail: this.paypalEmail,
          active: this.active,
          isSubAccount: this.isSubAccount,
          isDepreciation: this.isDepreciation,
          asOf: this.convertDateForBackend(this.asOf),
          comments: this.comments,
          parentAccountId: this.isSubAccount ? (this.parentAccountId === '' ? null : this.parentAccountId) : null,
          openingBalance: this.openingBalance === '' ? null : Number(this.openingBalance),
          depositAmount: this.isEdit ? this.depositAmount : undefined,
        };

        if (this.isEdit) {
          await ApiService.put(`/company-account/${this.editAccountId}`, payload);
          this.$toast && this.$toast.success('Account updated successfully!');
          if (goToNew) {
            await this.saveAndNew();
          } else {
            this.saveAndClose();
          }
        } else {
          await axios.post('/company-account/create', payload);
          this.$toast && this.$toast.success('Account created successfully!');
          if (goToNew) {
            await this.saveAndNew();
          } else {
            this.saveAndClose();
          }
        }
      } catch (error) {
        const errorMsg = error.response?.data?.message || error.message || 'An unexpected error occurred';
        this.showError(`Error ${this.isEdit ? 'updating' : 'creating'} account: ${errorMsg}`);
      } finally {
        this.saving = false;
      }
    },
    saveAndClose() {
      this.$router.push(`/company/${this.$route.params.id}/dashboard/company-account-list`);
    },
    async saveAndNew() {
      this.accountNumber = '';
      this.accountName = '';
      this.bankName = '';
      this.bankAccountType = '';
      this.bankAccountNumber = '';
      this.creditLimit = '';
      this.cardOrLoanNumber = '';
      this.paypalEmail = '';
      this.active = true;
      this.isSubAccount = false;
      this.isDepreciation = false;
      this.asOf = this.formatDateForDatePicker(new Date());
      this.comments = '';
      this.parentAccountId = '';
      this.openingBalance = 0.0;
      this.depositAmount = 0;

      await this.fetchExistingAccounts();

      if (this.accountType) {
        await this.generateNextAccountNumber();
      }
    },
    cancel() {
      this.$router.push(`/company/${this.$route.params.id}/dashboard/overview`);
    },
    showError(message) {
      this.errorMessage = message;
      this.showErrorModal = true;
    },
    closeErrorModal() {
      this.showErrorModal = false;
      this.errorMessage = '';
    },
  },
  watch: {
    async accountType(newType, oldType) {
      if (newType && !this.isEdit && newType !== oldType) {
        if (this.existingAccounts.length === 0) {
          await this.fetchExistingAccounts();
        }
        this.accountNumber = '';
        await this.generateNextAccountNumber();
      }

      if (newType !== 'Bank') {
        this.fieldErrors.bankName = '';
        this.fieldErrors.bankAccountType = '';
        this.fieldErrors.bankAccountNumber = '';
      }
    },
    accountNumber() {
      if (this.fieldErrors.accountNumber && this.accountNumber.trim()) {
        this.fieldErrors.accountNumber = '';
      }
      if (this.accountNumber.trim() && this.accountName.trim()) {
        this.validateDuplicateAccount();
      }
    },
    accountName() {
      if (this.fieldErrors.accountName && this.accountName.trim()) {
        this.fieldErrors.accountName = '';
      }
      if (this.accountNumber.trim() && this.accountName.trim()) {
        this.validateDuplicateAccount();
      }
    },
    bankName() {
      if (this.fieldErrors.bankName && this.bankName.trim()) {
        this.fieldErrors.bankName = '';
      }
    },
    bankAccountType() {
      if (this.fieldErrors.bankAccountType && this.bankAccountType) {
        this.fieldErrors.bankAccountType = '';
      }
    },
    bankAccountNumber() {
      if (this.fieldErrors.bankAccountNumber && this.bankAccountNumber.trim()) {
        this.fieldErrors.bankAccountNumber = '';
      }
      if (this.accountType === 'Bank' && this.bankAccountNumber.trim()) {
        this.validateDuplicateAccount();
      }
    },
    parentAccountId() {
      if (this.parentAccountId && this.parentAccountId !== '') {
        this.parentAccountError = '';
        this.fieldErrors.parentAccount = '';
      }
    },
    isSubAccount() {
      if (!this.isSubAccount) {
        this.parentAccountError = '';
        this.fieldErrors.parentAccount = '';
      }
    },
  },
  async created() {
    this.asOf = this.formatDateForDatePicker(new Date());

    const route = this.$route;
    const editMatch = route.path.match(/edit-account\/(\w+)/);
    if (editMatch) {
      this.isEdit = true;
      this.editAccountId = editMatch[1];
      await this.fetchAccountForEdit();
    } else {
      await this.fetchExistingAccounts();
    }

    try {
      const companyId = this.$route.params.id;
      const res = await ApiService.getCompanyAccounts(companyId);
      const allAccounts = res.data.accounts || res.data || [];
      this.parentAccounts = allAccounts.filter((acc) => {
        if (this.isEdit && this.editAccountId) {
          return !acc.isSubAccount && String(acc.id) !== String(this.editAccountId);
        }
        return !acc.isSubAccount;
      });
    } catch (error) {
      logger.error('Failed to fetch parent accounts:', error);
      this.parentAccounts = [];
    }
  },
};
</script>
